// EventCheckin.jsx - Standalone page for on-site event check-in
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EventCheckinScreen from '../components/EventCheckinScreen';
import { EventbriteApiService } from '../utils/eventApiHelpers';
import { ArrowLeft, Settings, Download, BarChart3 } from 'lucide-react';
import '../styles/EventCheckinScreen.css';

const EventCheckin = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadEventDetails();
    
    // For standalone check-in, we can work without a user token
    // The RPC functions should work with just the apikey
    setUserToken('standalone'); // Use a special token for standalone mode
  }, [eventId]);

  const loadEventDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use environment variables for Supabase configuration
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_event_details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ event_id_param: parseInt(eventId) })
      });

      if (response.ok) {
        const data = await response.json();
        setEventDetails(data.event);
      } else {
        throw new Error('Failed to load event details');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserTokenFromContext = () => {
    // Replace this with your actual auth token retrieval method
    // Examples:
    // - useAuth hook: const { token } = useAuth();
    // - Context: const { userToken } = useContext(AuthContext);
    // - Redux: const token = useSelector(state => state.auth.token);
    return null;
  };

  const exportAttendeeData = async () => {
    if (!eventDetails?.eventbrite_id || !userToken) return;
    
    try {
      const apiService = new EventbriteApiService(userToken);
      const data = await apiService.getEventAttendees(eventDetails.eventbrite_id, {
        limitVal: 1000 // Get all attendees for export
      });
      
      AttendeeUtils.exportAttendeesToCSV(data.attendees || [], eventDetails.name);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export attendee data');
    }
  };

  const goToAdmin = () => {
    // Navigate to collection admin if you have the collection ID
    // Otherwise, navigate to a general admin dashboard
    if (eventDetails?.collection_id) {
      navigate(`/collections/${eventDetails.collection_id}`);
    } else {
      navigate('/admin');
    }
  };

  if (loading) {
    return (
      <div className="checkin-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Event...</h2>
        <p>Preparing check-in interface for Event {eventId}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkin-error">
        <div className="error-content">
          <h2>Unable to Load Event</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft className="back-icon" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!eventDetails) {
    return (
      <div className="checkin-error">
        <div className="error-content">
          <h2>Event Not Found</h2>
          <p>Event ID {eventId} does not exist or you don't have access to it.</p>
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft className="back-icon" />
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-page">
      {/* Quick Actions Bar (Hidden by default, shown on gesture/click) */}
      <div className={`quick-actions ${showSettings ? 'visible' : ''}`}>
        <button onClick={() => setShowSettings(!showSettings)} className="settings-toggle">
          <Settings className="settings-icon" />
        </button>
        
        {showSettings && (
          <div className="actions-menu">
            <button onClick={goToAdmin} className="action-item">
              <BarChart3 className="action-icon" />
              Admin Dashboard
            </button>
            <button onClick={exportAttendeeData} className="action-item">
              <Download className="action-icon" />
              Export Data
            </button>
            <button onClick={() => navigate(-1)} className="action-item">
              <ArrowLeft className="action-icon" />
              Back
            </button>
          </div>
        )}
      </div>

      {/* Main Check-in Interface */}
      <EventCheckinScreen 
        eventId={eventId}
        eventDetails={eventDetails}
        userToken={userToken}
      />

      {/* Footer Info (For Debug/Staff) */}
      <div className="checkin-footer">
        <span>Event ID: {eventId}</span>
        {eventDetails.eventbrite_id && (
          <span>Eventbrite: {eventDetails.eventbrite_id}</span>
        )}
        <span>Updated: {new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  );
};

// QR Code Generator Component (Optional)
const QRCodeGenerator = ({ eventId }) => {
  const [qrCodeUrl, setQRCodeUrl] = useState('');
  
  useEffect(() => {
    const generateQR = async () => {
      try {
        const QRCode = await import('qrcode');
        const checkinUrl = `${window.location.origin}/event/${eventId}/checkin`;
        const qrDataUrl = await QRCode.toDataURL(checkinUrl, {
          width: 256,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQRCodeUrl(qrDataUrl);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      }
    };
    
    if (eventId) {
      generateQR();
    }
  }, [eventId]);

  if (!qrCodeUrl) return null;

  return (
    <div className="qr-code-section">
      <h3>Check-in QR Code</h3>
      <img src={qrCodeUrl} alt="Check-in QR Code" className="qr-code" />
      <p>Scan to access check-in screen</p>
    </div>
  );
};

export default EventCheckin;